<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import PopupForm from './phases/PhaseForm.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faChevronLeft,
    faChevronRight,
    faSquarePlus,
    faTrashCan,
    faPencil,
    faCircleDot,
  } from '@fortawesome/pro-light-svg-icons';
  
  // Using proxy endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  let phases = [];
  let show = false;
  let showForm = false;
  let showConfirmation = false;
  let editPhase = null;
  let phaseToDelete = null;
  let phaseId = null;

  onMount(async () => {
    //selectedStudy.set(null);
    selectedSite.set(null);
    selectedParticipant.set(null);

    if ($selectedStudy) {
      fetchPhases();
    }
  });

  async function fetchPhases() {
    try {
      const res = await fetch(`${API_BASE_URL}/phases/study/${$selectedStudy.studyId}`);

      if (!res.ok) {
        throw new Error('Failed to fetch phases');
      }

      phases = await res.json();
    } catch (err) {
      console.error('Error fetching phases:', err);
    }
  }

  async function handleCreatePhase(event) {
    const newPhase = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/phases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPhase),
      });

      if (!res.ok) {
        throw new Error('Failed to create phase');
      }

      await fetchPhases();
      showForm = false;
    } catch (err) {
      console.error('Error creating phase:', err);
    }
  }

  async function handleEditPhase(event) {
    const updatedPhase = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/phases/${updatedPhase._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedPhase),
      });

      if (!res.ok) {
        throw new Error('Failed to update phase');
      }

      await fetchPhases();
      showForm = false;
      editPhase = null;
    } catch (err) {
      console.error('Error updating phase:', err);
    }
  }

  async function confirmDelete() {
    if (phaseToDelete) {
      try {
        const res = await fetch(`${API_BASE_URL}/phases/${phaseToDelete._id}`, {
          method: 'DELETE',
        });

        if (!res.ok) {
          throw new Error('Failed to delete phase');
        }

        await fetchPhases();
        showConfirmation = false;
        phaseToDelete = null;
      } catch (err) {
        console.error('Error deleting phase:', err);
      }
    }
  }

  function handleDeleteClick(phase) {
    phaseToDelete = phase;
    showConfirmation = true;
  }

  function handlePhaseClick(phase) {}

  function handleEditClick(phase) {
    editPhase = phase;
    showForm = true;
  }

  function handleNewClick() {
    const studyId = $selectedStudy ? $selectedStudy.studyId : 'NONE';
    editPhase = null;
    showForm = true;
  }
</script>

<PopupForm
  {showForm}
  {editPhase}
  {phaseId}
  on:submit={editPhase ? handleEditPhase : handleCreatePhase}
  on:close={() => (showForm = false)}
/>

<ConfirmationModal
  {showConfirmation}
  message="Are you sure you want to delete this phase?"
  on:confirm={confirmDelete}
  on:close={() => (showConfirmation = false)}
/>

<div class="flex justify-between ph-2 pb-0">
  <h2 class="text-s mb-4 text-sky-800 dark:text-white">
    {$selectedStudy ? 'Study: ' + $selectedStudy.studyId + ' - Phases' : 'Phases'}
  </h2>
  <div class="text-black dark:text-white">
    <button on:click={handleNewClick} class="pagination mx-1 p-0">
      <FontAwesomeIcon icon={faSquarePlus} class="text-sky-800 dark:text-white" />
    </button>
  </div>
</div>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold">Name</th>
        <th class="px-4 py-2 text-left bold">Type</th>
        <th class="px-4 py-2 text-left bold">Duration</th>
        <th class="px-4 py-2 text-left bold">Order</th>
        <th class="px-4 py-2 text-left bold">Info</th>
        <th class="w-px px-4 py-2 text-left bold">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each phases as phase, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}>{phase.name}</td>
          <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}>{phase.type}</td>
          <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}
            >{phase.duration > 0 ? phase.duration + ' ' + phase.durationType : ''}</td
          >
          <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}>{phase.order}</td>
          <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}>{phase.info}</td>
          <td class="px-4 py-2">
            <button on:click={() => handleEditClick(phase)} class="pagination mx-1 p-0">
              <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
            </button>
            <button on:click={() => handleDeleteClick(phase)} class="pagination mx-1 p-0">
              <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
            </button>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>
